// src/services/aeo/devMock.ts
//
// DEV-ONLY fixtures for the AEO Assessment API (ourteam-frontend#3065). The
// real BE/BFF proxy that injects `X-AEO-Secret` doesn't exist yet, so this
// simulates the whole contract — queued → running → terminal (complete /
// partial+ENGINE_STUBBED / failed), 409 dedupe on a domain already in flight,
// and per-domain history — entirely in memory (a page reload resets it).
// Only ./index.ts imports this, and only behind `import.meta.env.DEV`.
//
// DELETE ME once the real proxy ships: remove this file and the
// `if (import.meta.env.DEV)` branches in index.ts that call into it. Nothing
// outside index.ts should ever import from here directly.
//
// Deterministic-but-varied outcomes so every ticket state is reachable from
// the UI without special test tooling — keyed off the domain the user types:
//   - contains "fail"          → terminal `failed` (SCRAPE_TIMEOUT)
//   - contains "full" / "demo" → terminal `complete` (realistic, non-zero numbers)
//   - anything else            → terminal `partial` + ENGINE_STUBBED, which is
//                                 today's actual BE behavior per #3065's
//                                 "Out of scope" note (goose-aeo not provisioned).

import type {
    AeoEngineShare,
    AeoGoogleAiRefreshJob,
    AeoGoogleAiRefreshStart,
    AeoGoogleAiRefreshState,
    AeoRerunKind,
    AeoStageRerunJob,
    AeoAssessment,
    AeoBusiness,
    AeoBusinessDetail,
    AeoCancelResult,
    AeoConnector,
    AeoKnowledgeSource,
    KnowledgeAddResult,
    KnowledgeContent,
    KnowledgePage,
    StagedDeleteResult,
    AeoCreateAssessmentRequest,
    AeoCreateResult,
    AeoDetectedBusiness,
    AeoDraft,
    AeoDraftCreateRequest,
    AeoPrescreenRequest,
    AeoPrescreenResponse,
    AeoPrescreenVerdict,
    AeoPublication,
    AeoResults,
    AeoTracker,
    AeoTrackerSummary,
    AeoTrackerTaskPatch,
    AeoReadabilityRefreshJob,
    AeoReadabilityRefreshStart,
    AeoQueuedFill,
    AeoSearchQuestions,
    AeoSearchPerformance,
    AeoScoreHistoryEntry,
    AeoBusinessSpend,
    AeoAiVisibility,
    AeoCompetitorWin,
    AeoProbe,
    AeoQuery,
    AeoVisibilityCheck,
    AeoVisibilityCheckName,
} from "./types";
import { nyonyaVisibility } from "@oura/fixtures/nyonya-visibility";
import { addDays, addMonths, termMonth } from "./clientDocsRules";

const STAGES = [
    "Resolving business",
    "Crawling site",
    "Probing ChatGPT",
    "Probing Gemini",
    "Checking Google",
    "Scoring",
    "Rendering report",
];

let store: AeoAssessment[] = [];
let counter = 0;

function nowIso(): string {
    return new Date().toISOString();
}

function titleCase(s: string): string {
    return s.replace(/[-_.]+/g, " ").trim().replace(/\b\w/g, (c) => c.toUpperCase());
}

function outcomeFor(domain: string): "complete" | "partial" | "failed" {
    const d = domain.toLowerCase();
    if (d.includes("fail")) return "failed";
    if (d.includes("full") || d.includes("demo")) return "complete";
    return "partial";
}

function buildResults(
    input: AeoCreateAssessmentRequest,
    outcome: "complete" | "partial",
): AeoResults {
    if (outcome === "partial") {
        // ENGINE_STUBBED — visibility numbers are 0/fixture per #3065 until
        // goose-aeo is provisioned; the audit itself still runs, degraded.
        return {
            visibility: {
                openai: { mention_rate: 0, citation_rate: 0, responses_sampled: 0 },
                gemini: { mention_rate: 0, citation_rate: 0, responses_sampled: 0 },
                share_of_voice: 0,
            },
            audit: { overall_score: 4.2, pages_scraped: 18, mode: "visibility_only" },
            maturity_level: 1,
            maturity_label: "Not yet visible",
            top_competitors: [],
            actual_cost_usd: 0.12,
        };
    }
    // "complete" — realistic, non-zero numbers so the finished report UI is
    // fully previewable. A 0% ChatGPT mention_rate is left in deliberately:
    // it's the normal SMB baseline the ticket asks the FE to frame as opportunity.
    const hash = input.domain.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    return {
        visibility: {
            openai: { mention_rate: 0, citation_rate: 0, responses_sampled: 50 },
            gemini: {
                mention_rate: Math.round((0.15 + (hash % 30) / 100) * 100) / 100,
                citation_rate: 0.12,
                responses_sampled: 50,
            },
            share_of_voice: Math.round((0.05 + (hash % 12) / 100) * 100) / 100,
        },
        audit: {
            overall_score: Math.round((3.5 + (hash % 6) * 0.3) * 10) / 10,
            pages_scraped: 12 + (hash % 20),
            mode: "full",
        },
        maturity_level: 1 + (hash % 4),
        maturity_label: ["Not yet visible", "Building blocks", "Emerging", "Established"][hash % 4],
        top_competitors: [
            {
                name: input.competitors?.[0] ? titleCase(input.competitors[0]) : "Rival Clinic",
                queries_appeared: 10 + (hash % 10),
            },
            { name: "Nearby Practice", queries_appeared: 4 + (hash % 6) },
        ],
        actual_cost_usd: 0.42 + (hash % 20) / 100,
    };
}

function runJob(id: string, input: AeoCreateAssessmentRequest): void {
    let i = 0;
    const startedAt = nowIso();
    const tick = window.setInterval(() => {
        const job = store.find((j) => j.assessment_id === id);
        if (!job) {
            window.clearInterval(tick);
            return;
        }
        // Terminal wins over the loop. Without this the next tick would set
        // `status = "running"` again and resurrect a run cancelled a moment ago
        // — the exact bug ourteam-backend#3982 calls out as easy to miss, so the
        // fixture must not be the one place it silently works.
        if (job.status === "cancelled") {
            window.clearInterval(tick);
            return;
        }
        if (i < STAGES.length) {
            job.status = "running";
            job.stage = STAGES[i];
            job.stages_complete = i;
            job.started_at = job.started_at ?? startedAt;
            i += 1;
        } else {
            const outcome = outcomeFor(input.domain);
            job.stages_complete = STAGES.length;
            job.completed_at = nowIso();
            if (outcome === "failed") {
                job.status = "failed";
                job.stage = "Failed";
                job.error_code = "SCRAPE_TIMEOUT";
                job.error_detail = `Could not reach ${input.domain} — the site timed out during crawl.`;
            } else {
                job.status = outcome;
                job.stage = "Done";
                job.results = buildResults(input, outcome);
                job.report_url = outcome === "complete" ? `https://reports.ourteam.ai/aeo/${id}.pdf` : null;
                if (outcome === "partial") job.error_code = "ENGINE_STUBBED";
            }
            window.clearInterval(tick);
        }
        store = [...store];
    }, 1800);
}

// --- detect / prefill fixtures (ourteam-backend#3687) -----------------------
//
// The real endpoint CRAWLS the site (schema.org LocalBusiness, og:title, meta
// keywords, an about/contact page for the city). This cannot be simulated
// honestly, so it is faked from the spelling of the domain — which is why it
// must never run outside the fixture mode: a guessed "dental clinic" reads
// exactly like a real crawl result and is impossible to tell apart in QA.

const CATEGORY_RULES: Array<{ match: RegExp; category: string; services: string[] }> = [
    { match: /dental|dent|smile/, category: "dental clinic", services: ["veneers", "Invisalign", "scaling & polishing"] },
    { match: /clinic|medi|health/, category: "medical clinic", services: ["general consultation", "health screening"] },
    { match: /cafe|coffee|kopi|roast/, category: "cafe", services: ["specialty coffee", "brunch", "catering"] },
    { match: /pilates|yoga|fit|gym|barre/, category: "fitness studio", services: ["reformer pilates", "group classes", "personal training"] },
    { match: /salon|hair|beauty|nail/, category: "beauty salon", services: ["haircut & styling", "coloring", "manicure"] },
    { match: /law|legal/, category: "law firm", services: ["conveyancing", "corporate advisory"] },
    { match: /table|dine|restaurant|kitchen|food/, category: "restaurant", services: ["dine-in", "private events", "catering"] },
];

const MY_CITIES = ["Kuala Lumpur", "Subang Jaya", "Petaling Jaya"];

export function detect(domain: string): Promise<AeoDetectedBusiness> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            const label = domain.split("/")[0].split(".")[0] ?? "";
            const business_name = label
                .replace(/[-_]+/g, " ")
                .trim()
                .replace(/\b\w/g, (c) => c.toUpperCase());
            const rule = CATEGORY_RULES.find((r) => r.match.test(domain.toLowerCase()));
            const hash = domain.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
            const city = domain.endsWith(".sg") ? "Singapore" : MY_CITIES[hash % MY_CITIES.length];
            resolve({
                business_name: business_name || "",
                category: rule?.category ?? "",
                // Matches the real BE: `primary` is seeded with the detected name,
                // NOT with invented neighbourhood names. Staff supply the areas.
                anchors: { primary: business_name ? [business_name] : [], city },
                services: rule?.services ?? [],
                confidence: business_name && (rule?.category || city) ? "high" : "low",
            });
        }, 1400);
    });
}

export function prescreen(input: AeoPrescreenRequest): Promise<AeoPrescreenResponse> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            const d = input.domain.toLowerCase();
            const verdict: AeoPrescreenVerdict =
                d.includes("rival") || d.includes("visible")
                    ? "VISIBLE"
                    : d.length % 3 === 0
                        ? "PARTIAL"
                        : "INVISIBLE";
            const notes: Record<AeoPrescreenVerdict, string> = {
                INVISIBLE: `Neither ChatGPT nor Gemini named ${input.business_name} for its core queries. This is the customer the pitch works on.`,
                PARTIAL: `${input.business_name} appears occasionally on one engine but not the other. Worth the full run.`,
                VISIBLE: `${input.business_name} is already named consistently across both engines.`,
            };
            resolve({ verdict, engines_checked: ["openai", "gemini"], notes: notes[verdict] });
        }, 1200);
    });
}

export function createAssessment(input: AeoCreateAssessmentRequest): Promise<AeoCreateResult> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            const inflight = store.find(
                (a) => a.domain === input.domain && (a.status === "queued" || a.status === "running"),
            );
            if (inflight) {
                resolve({ ok: false, kind: "duplicate", assessment_id: inflight.assessment_id });
                return;
            }
            if (!input.anchors?.primary?.length || !input.services?.length) {
                resolve({
                    ok: false,
                    kind: "validation",
                    errors: ["anchors.primary and services are required"],
                });
                return;
            }
            counter += 1;
            const id = `mock-aeo-${counter}-${Math.random().toString(36).slice(2, 8)}`;
            const job: AeoAssessment = {
                assessment_id: id,
                domain: input.domain,
                business_name: input.business_name,
                category: input.category,
                status: "queued",
                stage: "Queued",
                stages_complete: 0,
                stages_total: STAGES.length,
                created_at: nowIso(),
            };
            store = [job, ...store];
            runJob(id, input);
            resolve({
                ok: true,
                data: {
                    assessment_id: id,
                    status: "queued",
                    estimated_cost_usd: 0.6,
                    estimated_duration_minutes: 8,
                    queue_position: 1,
                },
            });
        }, 400);
    });
}

export function getAssessment(id: string): Promise<AeoAssessment> {
    return new Promise((resolve, reject) => {
        window.setTimeout(() => {
            const job = store.find((j) => j.assessment_id === id) ?? DEMO_PAST_RUNS.find((j) => j.assessment_id === id);
            if (!job) {
                reject(new Error("assessment not found"));
                return;
            }
            resolve({ ...job });
        }, 150);
    });
}

/**
 * POST /api/v1/assessments/{id}/cancel — mirrors the real cooperative cancel.
 *
 * Flags the row terminal and lets `runJob`'s loop notice at its next tick, the
 * same shape as the BE's stage-boundary check, so the fixture path exercises the
 * same race the real one has rather than a tidier one that hides it.
 */
export function cancelAssessment(id: string): Promise<AeoCancelResult> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            const job = store.find((j) => j.assessment_id === id);
            if (!job) {
                resolve({ ok: false, kind: "not_found" });
                return;
            }
            if (job.status !== "queued" && job.status !== "running") {
                resolve({ ok: false, kind: "already_terminal" });
                return;
            }
            job.status = "cancelled";
            job.stage = "Cancelled";
            job.completed_at = nowIso();
            store = [...store];
            resolve({ ok: true, status: "cancelled", stage: "Cancelled", cancelled_at: job.completed_at });
        }, 200);
    });
}

export function listAssessments(domain: string, limit = 20): Promise<AeoAssessment[]> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            resolve(
                store
                    .filter((j) => j.domain === domain)
                    .sort((a, b) => b.created_at.localeCompare(a.created_at))
                    .slice(0, limit)
                    .map((j) => ({ ...j })),
            );
        }, 150);
    });
}

// --- businesses fixtures (ourteam-backend#3747) ------------------------------
//
// Two groups of static demo rows so the Businesses table isn't empty on a
// fresh `npm run dev` with no scans run yet, plus one live row per domain
// scanned this session (newest terminal run per domain, from `store`) — so
// running a New Scan in fixture mode shows up here too, exactly like the
// real API.
//
// Group 1 — the three REAL src/oura/data.ts fixtures, listed here with their
/** Today in Malaysian time, DATE-ONLY: what the sample plans are dated from. */
const SAMPLE_TODAY = new Date(Date.now() + 8 * 3_600_000).toISOString().slice(0, 10);
const daysAgo = (n: number) => addDays(SAMPLE_TODAY, -n);

type SamplePlan = Pick<AeoBusiness,
    "engagement_status" | "timeline_published_version" | "lifecycle" | "plan_name"
    | "plan_term_months" | "plan_started_on" | "plan_ends_on" | "plan_month">;

/**
 * A signed plan on a sample row, dated from today so the Businesses tabs read
 * the same whenever the preview runs. `startedOn` null is signed with the
 * clock not started. The term ends the way the timeline engine ends one
 * (`aeo_timeline`: the anniversary, less a day), and the server sends
 * `engagement_status: "signed"` and an `active` lifecycle even once that day
 * has passed, which is the case the list's own end-date check exists for.
 */
function samplePlan(name: string, termMonths: number, startedOn: string | null, published = true): SamplePlan {
    const running = !!startedOn && startedOn <= SAMPLE_TODAY;
    return {
        engagement_status: "signed",
        timeline_published_version: published ? 1 : null,
        lifecycle: { state: running ? "active" : "signed", since: startedOn },
        plan_name: name,
        plan_term_months: termMonths,
        plan_started_on: startedOn,
        plan_ends_on: startedOn ? addDays(addMonths(startedOn, termMonths), -1) : null,
        plan_month: startedOn ? termMonth(startedOn, SAMPLE_TODAY, termMonths) : null,
    };
}

// EXACT real ids/domains/names so isFixtureBusiness (oura-workspace/index.tsx)
// correctly routes a click to the full rich fixture detail experience
// (BusinessOverview/SiteAudit/Questions fully populated), not the thin
// live-data adapter path. Without an entry here matching their real id, the
// table has no row that links to them at all — they existed but were
// unreachable from the list after #3152 switched it to read from
// listBusinesses() instead of data.ts (see ourteam-frontend#3160).
const FIXTURE_BUSINESSES: AeoBusiness[] = [
    {
        id: "pilates-collective",
        domain: "thepilatescollective.my",
        business_name: "The Pilates Collective",
        category: "Pilates studio",
        city: "Kuala Lumpur",
        state: "client",
        plan: "growth",
        // Running, with no timeline published yet: the plan queue on Active plans.
        ...samplePlan("Annual plan", 12, daysAgo(10), false),
        // Matches the real fixture: gated: true in pilates-collective.ts —
        // this row exists specifically to demo the gated state.
        created_at: "2026-03-12T09:20:00Z",
        latest_assessment: {
            assessment_id: "pilates-collective-latest",
            status: "failed",
            captured_at: "2026-07-08T04:00:00Z",
            aeo_score: 0,
            seo_score: 0,
            gated: true,
            // Exercises the gated-reason panel: without these the new state can
            // only be seen against a genuinely broken live run, which is not a
            // thing anyone can reproduce on demand.
            error_code: "SITE_UNREACHABLE",
            error_detail: "https://thepilatescollective.my returned no response after 30s (3 attempts).",
            stage: "Crawling site",
            engine_version: "goose-aeo@0.2.2",
        },
    },
    {
        id: "nyonya-table",
        domain: "nyonyatable.com",
        business_name: "Nyonya Table",
        category: "Restaurant",
        city: "Bukit Bintang",
        state: "client",
        plan: "track",
        // Running, and its set-up still open, so it waits on Onboarding.
        ...samplePlan("Annual plan", 12, daysAgo(44)),
        created_at: "2026-04-02T02:15:00Z",
        latest_assessment: {
            assessment_id: "nyonya-table-latest",
            status: "complete",
            captured_at: "2026-09-16T08:00:00Z",
            aeo_score: 47,
            seo_score: 63,
            gated: false,
        },
    },
    {
        id: "new-clinic",
        domain: "riversidedental.co",
        business_name: "Riverside Dental",
        category: "Dental clinic",
        city: "Petaling Jaya",
        state: "prospect",
        plan: null,
        created_at: "2026-06-28T07:45:00Z",
        latest_assessment: {
            assessment_id: "new-clinic-latest",
            status: "complete",
            captured_at: "2026-07-07T04:00:00Z",
            aeo_score: 29,
            seo_score: 52,
            gated: false,
        },
    },
];

/**
 * The null the backend sends for a score it could not measure (#4096, #4780).
 * `AeoBusinessLatestAssessment.seo_score`, `AeoBusinessAudit.siteHealthScore`
 * and `siteHealthVerdict` are still typed as always present, so the demo
 * states the wire value through this one cast rather than a made-up number.
 */
const WIRE_NULL = null as unknown as never;

// Group 2 — deliberately named/domained so they can NEVER be confused with
// the real fixtures above. These preview the businesses LIST + New Scan flow
// as REAL API rows (i.e. what a business looks like when it did NOT come
// from a bundled fixture) — a prior version of this file used "The Pilates
// Collective" here too, which caused exactly that confusion (looked like the
// real fixture, silently carried none of its data) — see ourteam-frontend#3160.
const DEMO_BUSINESSES: AeoBusiness[] = [
    {
        id: "mock-list-scored",
        domain: "mockbiz-scored.demo",
        business_name: "[Mock] Scored Business",
        category: "Fitness Studio",
        city: "Subang Jaya",
        state: "client",
        plan: "growth",
        // Ends in 40 days: inside the renewal window.
        ...samplePlan("6-Month plan", 6, addMonths(addDays(SAMPLE_TODAY, 41), -6)),
        created_at: "2026-05-19T11:05:00Z",
        latest_assessment: {
            assessment_id: "mock-list-scored-latest",
            status: "complete",
            captured_at: "2026-07-20T04:00:00Z",
            aeo_score: 41,
            seo_score: 58,
            gated: false,
        },
    },
    {
        id: "mock-list-pending",
        domain: "mockbiz-pending.demo",
        business_name: "[Mock] Not-Yet-Scored Business",
        category: "Dental Clinic",
        city: "Kuala Lumpur",
        state: "prospect",
        plan: null,
        created_at: "2026-07-24T03:30:00Z",
        latest_assessment: null, // demonstrates the "no terminal run yet" pending state
    },
    {
        id: "mock-list-crawl-failed",
        domain: "mockbiz-unreachable.demo",
        business_name: "[Mock] Crawl-Failed Business",
        category: "Beauty Salon",
        city: "Petaling Jaya",
        state: "prospect",
        plan: null,
        created_at: "2026-07-02T06:10:00Z",
        latest_assessment: {
            assessment_id: "mock-list-crawl-failed-latest",
            status: "complete",
            captured_at: "2026-07-22T04:00:00Z",
            aeo_score: 36,
            // AI visibility was measured and the site was not (#4780): the
            // list carries the one score and no site score.
            seo_score: WIRE_NULL,
            gated: false,
        },
    },
    {
        // The term's last day was three weeks ago. The server still says
        // signed and active; the list ends it on the date.
        id: "mock-list-ended",
        domain: "mockbiz-ended.demo",
        business_name: "[Mock] Ended Client",
        category: "Physiotherapy",
        city: "Shah Alam",
        state: "client",
        plan: "growth",
        ...samplePlan("Annual plan", 12, addMonths(daysAgo(20), -12)),
        created_at: "2025-08-30T02:00:00Z",
        latest_assessment: {
            assessment_id: "mock-list-ended-latest",
            status: "complete",
            captured_at: "2026-08-28T04:00:00Z",
            aeo_score: 62,
            seo_score: 71,
            gated: false,
        },
    },
    {
        // An online store on a running plan: it has no town, it sells to a country.
        id: "mock-list-online",
        domain: "mockbiz-online.demo",
        business_name: "[Mock] Online Store",
        category: "Baby products",
        city: "Australia",
        state: "client",
        plan: "growth",
        buy: "online",
        market: "Australia",
        ...samplePlan("6-Month plan", 6, daysAgo(70)),
        created_at: "2026-07-30T02:00:00Z",
        latest_assessment: {
            assessment_id: "mock-list-online-latest",
            status: "complete",
            captured_at: "2026-10-12T04:00:00Z",
            aeo_score: 46,
            seo_score: 54,
            gated: false,
        },
    },
    {
        // An online prospect scanned before the New scan asked how it sells:
        // no `buy` on the row, so the list reads the country in `city`.
        id: "mock-list-online-prospect",
        domain: "mockbiz-online-sg.demo",
        business_name: "[Mock] Online Prospect",
        category: "Skincare",
        city: "Singapore",
        state: "prospect",
        plan: null,
        created_at: "2026-09-21T02:00:00Z",
        latest_assessment: {
            assessment_id: "mock-list-online-prospect-latest",
            status: "complete",
            captured_at: "2026-09-21T04:00:00Z",
            aeo_score: 31,
            seo_score: 49,
            gated: false,
        },
    },
    {
        // Goes to its customers: a city, and no outlet to visit.
        id: "mock-list-home-visits",
        domain: "mockbiz-homephysio.demo",
        business_name: "[Mock] Home Physio",
        category: "Physiotherapy",
        city: "Kuala Lumpur",
        state: "prospect",
        plan: null,
        buy: "we_go_to_them",
        market: null,
        created_at: "2026-10-02T02:00:00Z",
        latest_assessment: {
            assessment_id: "mock-list-home-visits-latest",
            status: "complete",
            captured_at: "2026-10-03T04:00:00Z",
            aeo_score: 27,
            seo_score: 61,
            gated: false,
        },
    },
];

// fixture-only: an older run of the live-shaped demo business that failed, so
// opening it from the scan picker (?scan=) walks the "this scan produced no
// score" state. getAssessment() finds it here once the store has no match.
const DEMO_PAST_RUNS: AeoAssessment[] = [
    {
        assessment_id: "mock-list-scored-failed",
        domain: "mockbiz-scored.demo",
        business_name: "[Mock] Scored Business",
        category: "Fitness Studio",
        status: "failed",
        stage: "Crawling site",
        created_at: "2026-06-22T03:52:00Z",
        completed_at: "2026-06-22T04:00:00Z",
        error_code: "SITE_UNREACHABLE",
        error_detail: "mockbiz-scored.demo did not answer within 60 seconds.",
    },
];

// fixture-only: the scan list for the samples that have one, so History, the
// scan picker and a past scan's read-only state can be walked on fixtures.
// Nyonya Table lists the three scans its Visibility fixture plots, so the two
// screens agree; the demo business has its latest scan and the failed run.
const SCORE_HISTORY: Record<string, AeoScoreHistoryEntry[]> = {
    "nyonya-table": nyonyaVisibility.history,
    "mock-list-scored": [
        { assessment_id: "mock-list-scored-latest", captured_at: "2026-07-20T04:00:00Z", aeo_score: 41, seo_score: 58, gated: false, status: "complete" },
        { assessment_id: "mock-list-scored-failed", captured_at: "2026-06-22T04:00:00Z", aeo_score: null, seo_score: null, gated: false, status: "failed" },
    ],
};

/**
 * fixture-only: which sample scans staff hid from the client, or published
 * after they were held. Kept in this browser so a staff tab and a client tab
 * on the same preview agree. A scan with no entry is as its fixture says.
 */
const PUBLISHED_KEY = "oura-fixture-published";
function readPublished(): Record<string, string | null> {
    try {
        return JSON.parse(window.localStorage.getItem(PUBLISHED_KEY) ?? "{}") as Record<string, string | null>;
    } catch {
        return {};
    }
}

/** A sample scan's `published_at`, after any publish or hide on this preview. */
export function fixturePublishedAt(entry: AeoScoreHistoryEntry): AeoScoreHistoryEntry {
    const saved = readPublished();
    return entry.assessment_id in saved ? { ...entry, published_at: saved[entry.assessment_id] } : entry;
}

/** The version publication route's fixture: every scan of the version at once. */
export function setScansPublished(assessmentIds: string[], on: boolean): Promise<{ ok: true }> {
    const publishedAt = on ? new Date().toISOString() : null;
    try {
        const saved = readPublished();
        assessmentIds.forEach((id) => {
            saved[id] = publishedAt;
        });
        window.localStorage.setItem(PUBLISHED_KEY, JSON.stringify(saved));
    } catch {
        // Private window: the change lasts until the page is reloaded.
    }
    return new Promise((resolve) => window.setTimeout(() => resolve({ ok: true }), 400));
}

/** GET /businesses/{id}/score-history fixture, newest first. Empty for the other samples. */
export function getScoreHistory(businessId: string): AeoScoreHistoryEntry[] {
    return (SCORE_HISTORY[businessId] ?? []).map((entry) => fixturePublishedAt({ ...entry }));
}

/** The Monday (local time) of the week `d` falls in, as YYYY-MM-DD. */
function mondayOf(d: Date): string {
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    day.setDate(day.getDate() - ((day.getDay() + 6) % 7));
    return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
}

/** GET /businesses/{id}/spend fixture: the sample's recorded scan spend by week, plus a doc draft. */
export function getBusinessSpend(businessId: string, weeks: number): AeoBusinessSpend {
    const now = new Date();
    const starts = Array.from({ length: weeks }, (_, i) => {
        const d = new Date(now);
        d.setDate(d.getDate() - 7 * (weeks - 1 - i));
        return mondayOf(d);
    });
    const rows = starts.map((start) => ({ start, totalUsd: 0, bySource: { scans: 0, docSummaries: 0, skillRuns: 0 } }));
    let scans = 0;
    let unknown = 0;
    const history = getScoreHistory(businessId);
    for (const h of history) {
        const usd = h.cost?.actual_usd;
        if (typeof usd !== "number") { unknown += 1; continue; }
        scans += usd;
        const row = rows.find((r) => r.start === mondayOf(new Date(h.captured_at)));
        if (row) row.bySource.scans += usd;
    }
    const drafts = history.length > 0 ? 0.9 : 0;
    if (drafts && rows.length > 1) rows[rows.length - 2].bySource.skillRuns += drafts;
    for (const r of rows) r.totalUsd = r.bySource.scans + r.bySource.docSummaries + r.bySource.skillRuns;
    return {
        currency: "USD",
        totalUsd: scans + drafts,
        sources: [
            { key: "scans", label: "Scans", totalUsd: scans, items: history.length, unknown },
            { key: "docSummaries", label: "Doc summaries", totalUsd: 0, items: 0, unknown: 0 },
            { key: "skillRuns", label: "Doc drafts", totalUsd: drafts, items: drafts ? 1 : 0, unknown: 0 },
        ],
        weeks: rows,
        thisWeekUsd: rows[rows.length - 1].totalUsd,
        lastWeekUsd: rows.length > 1 ? rows[rows.length - 2].totalUsd : null,
        notCounted: [
            "Search result lookups during a scan (DataForSEO, Bright Data)",
            "Copy Pack, Answer Plan and GBP Pack writing",
            "Competitor discovery and rival name clean-up",
            "Judged coverage, transplant fill and ChatGPT app capture",
            "Client request reads (shared across clients, not linked to one)",
        ],
    };
}

/**
 * Businesses another mock store created (onboarding creates a client before
 * any scan). Registered here so the list and the detail can both find them.
 */
const REGISTERED_BUSINESSES: AeoBusiness[] = [];
export function registerMockBusiness(row: AeoBusiness): void {
    const at = REGISTERED_BUSINESSES.findIndex((b) => b.id === row.id);
    // Registered again, the newer row wins: its first scan may have finished.
    if (at >= 0) REGISTERED_BUSINESSES[at] = row;
    else REGISTERED_BUSINESSES.push(row);
}

/** GET /api/v1/assessments with no domain — every mocked run, newest first. */
export function listAllAssessments(limit = 50): Promise<AeoAssessment[]> {
    return new Promise((resolve) => {
        window.setTimeout(
            () => resolve(store.slice(0, limit).map((j) => ({ ...j }))),
            150,
        );
    });
}

/** Soft-deleted domains, so the fixture list hides them like the real BE does. */
const deletedDomains = new Set<string>();

/** DELETE /api/v1/businesses/{id} — mirrors the BE's soft delete. */
export function deleteBusiness(id: string): Promise<boolean> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            // `id` may be a uuid-ish id OR a domain, matching resolve_business().
            const all = [...FIXTURE_BUSINESSES, ...DEMO_BUSINESSES];
            const hit = all.find((b) => b.id === id || b.domain === id);
            deletedDomains.add(hit?.domain ?? id);
            resolve(true);
        }, 200);
    });
}

export function listBusinesses(): Promise<AeoBusiness[]> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            const seen = new Set<string>();
            const fromScans: AeoBusiness[] = [];
            for (const a of store) {
                // domain is always set by createAssessment's mock job literal
                // below; the `?` on AeoAssessment.domain exists for the real BE
                // shape, not this store.
                const domain = a.domain ?? "";
                if (!domain || seen.has(domain)) continue;
                seen.add(domain);
                const terminal = a.status === "complete" || a.status === "partial";
                fromScans.push({
                    id: domain,
                    domain,
                    // A business created by scanning in this session entered the
                    // pipeline when that scan was requested — there is no earlier
                    // date to claim.
                    created_at: a.created_at,
                    business_name: a.business_name || domain,
                    category: a.category || "",
                    city: "",
                    state: "prospect",
                    plan: null,
                    latest_assessment: terminal
                        ? {
                            assessment_id: a.assessment_id,
                            status: a.status,
                            captured_at: a.completed_at ?? a.created_at,
                            // No 0-100 score exists on the mock assessment shape —
                            // approximate from the 0-10 audit score so the row has
                            // something plausible to render, not a real formula.
                            aeo_score: Math.min(100, Math.round((a.results?.audit.overall_score ?? 0) * 10)),
                            seo_score: Math.min(100, Math.round((a.results?.audit.overall_score ?? 0) * 12)),
                            gated: false,
                        }
                        : null,
                });
            }
            resolve(
                [
                    ...FIXTURE_BUSINESSES,
                    ...DEMO_BUSINESSES,
                    // A registered client that has since been scanned is the
                    // scan's row from then on.
                    ...REGISTERED_BUSINESSES.filter((r) => !seen.has(r.domain)),
                    ...fromScans,
                ].filter((b) => !deletedDomains.has(b.domain)),
            );
        }, 200);
    });
}

// ─── [Mock] Scored Business: a live-shaped Visibility payload ───────────────
// The sample businesses above read their own fixture files; this one goes
// through the live adapter (visibility/fromLive.ts) exactly as a real
// business does, so the paths only live data takes can be previewed: the
// ChatGPT app as the verdict with an `openai` row per question kept for its
// links, one app answer withheld, and a rival the API alone named.
// Consistent with businessAudit.competitors below: Reform Studio KL on 9
// questions, Core Collective on 4, counted the backend's way (every probe).

const MOCK_SCORED_AT = "2026-07-20T04:00:00Z";
const MOCK_RIVAL = { R: "Reform Studio KL", C: "Core Collective", P: "Pulse Barre Subang" } as const;
type MockRival = keyof typeof MOCK_RIVAL;

/** [question, check, app verdict (1 named, 0 not, "w" withheld), Gemini named, app rivals, Gemini rivals, API-only rivals] */
const MOCK_SCORED_ROWS: [string, AeoVisibilityCheckName, 0 | 1 | "w", 0 | 1, string, string, string][] = [
    ["pilates studio Subang Jaya", "location", 1, 1, "R", "R", ""],
    ["reformer pilates near me Subang Jaya", "location", 0, 0, "RC", "R", "P"],
    ["best fitness studio SS15", "location", 0, 1, "R", "C", ""],
    ["pilates class Sunway", "location", "w", 0, "", "R", "P"],
    ["barre class Subang Jaya", "location", 0, 0, "R", "R", ""],
    ["prenatal pilates Subang Jaya", "specialty", 1, 0, "", "R", ""],
    ["small group reformer class Subang Jaya", "specialty", 0, 0, "RC", "C", ""],
    ["pilates for back pain Petaling Jaya", "specialty", 0, 1, "", "", ""],
    ["beginner pilates course price Subang Jaya", "intent", 0, 0, "R", "R", ""],
    ["pilates trial class Subang Jaya", "intent", 1, 1, "C", "", ""],
    ["pilates membership Subang Jaya", "intent", 0, 0, "", "", ""],
    ["book a reformer class tonight Subang Jaya", "intent", 0, 0, "R", "R", ""],
];

const mockNames = (codes: string) => codes.split("").filter(Boolean).map((c) => MOCK_RIVAL[c as MockRival]);

function mockAnswer(named: boolean, rivals: string[]): string {
    const list = [...(named ? ["[Mock] Scored Business"] : []), ...rivals];
    if (list.length === 0) return "Several studios in Subang Jaya run these classes; check recent reviews before you book.";
    return `Studios worth a look: ${list.map((n) => `**${n}**`).join(", ")}. Book ahead for evening slots.`;
}

const mockScoredProbes: AeoProbe[] = MOCK_SCORED_ROWS.flatMap(([query, , app, gem, appRivals, gemRivals, apiOnly], i) => {
    const base = { query, query_id: `q${i + 1}`, country: "MY", cited: false, cited_pages: [], sentiment: null, sentiment_score: null };
    const appNames = mockNames(appRivals);
    const gemNames = mockNames(gemRivals);
    // The API's links-only row names what the app named, plus its own extras.
    const apiNames = [...appNames, ...mockNames(apiOnly)];
    return [
        app === "w"
            ? {
                ...base, engine: "chatgpt_app", instrument: "chatgpt_app", engineLabel: "ChatGPT (app)", mentioned: false, withheld: true,
                answer: "Log in or sign up to keep chatting.", rivals: [], rivalNames: [],
            }
            : {
                ...base, engine: "chatgpt_app", instrument: "chatgpt_app", engineLabel: "ChatGPT (app)", mentioned: app === 1,
                answer: mockAnswer(app === 1, appNames), rivals: appNames, rivalNames: appNames,
            },
        {
            ...base, engine: "openai", instrument: "openai", engineLabel: "OpenAI (API)", mentioned: false, cited: true,
            cited_pages: ["https://www.classpass.com/search/subang-jaya/pilates"], answer: mockAnswer(false, apiNames),
            rivals: apiNames, rivalNames: apiNames,
        },
        {
            ...base, engine: "gemini", instrument: "api", engineLabel: "Gemini", mentioned: gem === 1, cited: gem === 1,
            cited_pages: gem === 1 && i === 0 ? ["https://mockbiz-scored.demo/classes"] : [], answer: mockAnswer(gem === 1, gemNames),
            rivals: gemNames, rivalNames: gemNames,
        },
    ];
});

const mockScoredChecks: Partial<Record<AeoVisibilityCheckName, AeoVisibilityCheck>> = {};
(["location", "specialty", "intent"] as const).forEach((check) => {
    const rows = MOCK_SCORED_ROWS.filter((r) => r[1] === check);
    const mentioned = rows.reduce((n, r) => n + (r[2] === 1 ? 1 : 0) + r[3], 0);
    const answered = rows.reduce((n, r) => n + (r[2] === "w" ? 0 : 1) + 1, 0);
    mockScoredChecks[check] = {
        mentioned, answered, label: `${mentioned} of ${answered} answers`, unit: "answers",
        questions: rows.length, questionsAnswered: rows.length,
        questionsNamed: rows.filter((r) => r[2] === 1 || r[3] === 1).length, questionsExcluded: 0,
        question: check === "location" ? "Can AI find you where you are?" : check === "specialty" ? "Does AI know what you actually do?" : "Does AI surface you at the moment of decision?",
        fix: check === "location" ? "Listings and a page per area served" : check === "specialty" ? "Service pages, structured data and clearer positioning" : "Price pages, comparison pages and booking links",
    };
});

/** Tallied over every probe, as the backend does: the API-only rival is on it. */
const mockScoredWins: AeoCompetitorWin[] = (Object.keys(MOCK_RIVAL) as MockRival[]).map((code) => {
    const queries = MOCK_SCORED_ROWS.filter((r) => `${r[4]}${r[5]}${r[6]}`.includes(code)).map((r) => r[0]);
    return { name: MOCK_RIVAL[code], queryCount: queries.length, queries };
});

const mockAppNamed = MOCK_SCORED_ROWS.filter((r) => r[2] === 1).length;
const mockAppAnswered = MOCK_SCORED_ROWS.filter((r) => r[2] !== "w").length;
const mockGemNamed = MOCK_SCORED_ROWS.filter((r) => r[3] === 1).length;

const mockScoredVisibility: AeoAiVisibility = {
    score: 29,
    scoreAbsent: null,
    verdict: "Low",
    blurb: "Named on a few questions; rivals take most of the rest.",
    capturedAt: MOCK_SCORED_AT,
    engineRates: {
        chatgpt_app: { mentioned: mockAppNamed, answered: mockAppAnswered },
        gemini: { mentioned: mockGemNamed, answered: MOCK_SCORED_ROWS.length },
    },
    questionsNamed: MOCK_SCORED_ROWS.filter((r) => r[2] === 1 || r[3] === 1).length,
    // fixture-only until ourteam-backend handoff: Named-count denominator
    questionsOf: MOCK_SCORED_ROWS.length,
    mentions: mockAppNamed + mockGemNamed,
    mentionsDeltaPct: null,
    citationShare: { count: 1, denominator: MOCK_SCORED_ROWS.length, share: 1 / MOCK_SCORED_ROWS.length, absent: null, engines: ["gemini", "openai"], basis: "questions_own_domain_cited" },
    citations: 1,
    citationsDeltaPct: null,
    citationsFrom: ["gemini", "openai"],
    citedPages: 12,
    citedPagesDeltaPct: null,
    coverage: {
        headlineExcluded: 0, brandedExcluded: 0, enginesProbed: 2, enginesTotal: 2,
        questionsAsked: MOCK_SCORED_ROWS.length, questionsAnswered: MOCK_SCORED_ROWS.length,
        answersScored: mockAppAnswered + MOCK_SCORED_ROWS.length, recipe: "visibility-set-v4.2", crossChecked: false,
    },
    sourceDomains: [
        { domain: "classpass.com", answers: 11, own: false },
        { domain: "mockbiz-scored.demo", answers: 1, own: true },
    ],
    sourceDomainsTotal: 2,
    sourceDomainsBasis: "questions",
    ownDomainAnswers: 1,
    trend: [mockAppNamed + mockGemNamed],
    trendBasis: "per_assessment",
    trendCapturedAt: [MOCK_SCORED_AT],
    engines: [
        { engine: "ChatGPT (app)", instrument: "chatgpt_app", isChatgptApp: true, probed: true, mentions: mockAppNamed, sharePct: Math.round((mockAppNamed / mockAppAnswered) * 100), summary: "", rivals: [MOCK_RIVAL.R, MOCK_RIVAL.C] },
        { engine: "OpenAI (API)", instrument: "openai_api", citationOnly: true, probed: true, mentions: 0, sharePct: 0, summary: "", rivals: [MOCK_RIVAL.R, MOCK_RIVAL.P] },
        { engine: "Gemini", instrument: "api", probed: true, mentions: mockGemNamed, sharePct: Math.round((mockGemNamed / MOCK_SCORED_ROWS.length) * 100), summary: "", rivals: [MOCK_RIVAL.R, MOCK_RIVAL.C] },
        { engine: "Google AI", instrument: "api", probed: false, skipReason: "not_supported", mentions: 0, sharePct: 0, summary: "Google AI Overviews were not read on this scan.", rivals: [] },
    ],
    countries: [],
    checks: mockScoredChecks,
    competitorWins: mockScoredWins,
};

const mockScoredQueries: AeoQuery[] = MOCK_SCORED_ROWS.map((r, i) => ({ text: r[0], bucket: r[1], group: r[1], check: r[1], n: i + 1, slot_n: i + 1, language: "en" }));

const DEMO_AUDIT: Record<string, NonNullable<AeoBusinessDetail["latest_assessment"]>["results"]> = {
    "mock-list-scored": {
        aiVisibility: mockScoredVisibility,
        queries: mockScoredQueries,
        probes: mockScoredProbes,
        grounding: {
            instrument: "chatgpt_app", instrumentLabel: "ChatGPT (app)", instrumentNote: null, mixed: false,
            askedQuestions: MOCK_SCORED_ROWS.length, totalQuestions: MOCK_SCORED_ROWS.length, citationsMeasurable: false,
        },
        businessAudit: {
            siteHealthScore: 58,
            aeoScore: 41,
            // The backend's own sentence for 58 (`_band(score, "seo")`).
            siteHealthVerdict: "Developing SEO fundamentals, clear upside",
            aeoVerdict: "Developing AI visibility, clear upside",
            // The readability block as a refresh leaves it (#3831): measured two
            // days after the scan, 4 of the 5 checks shipped, and the crawl
            // behind it, so every line the Site screen reads from it shows.
            aiReadabilityScore: 59,
            aiReadabilityVerdict: "Developing AI readability, clear upside",
            aiReadabilityAbsent: null,
            aiReadabilityComponents: [
                { key: "headings", label: "Pages with a heading", covered: 21, applicable: 22, score: 95 },
                { key: "entity_markup", label: "Pages with entity markup", covered: 14, applicable: 22, score: 64 },
                { key: "prices", label: "Treatment pages with a price", covered: 3, applicable: 6, score: 50 },
                { key: "staff", label: "Staff pages linked to the business", covered: 1, applicable: 4, score: 25 },
            ],
            aiReadabilityMeasuredAt: "2026-07-22T03:00:00Z",
            aiReadabilityCrawl: { pagesCrawled: 24, readablePages: 22, readableShare: 22 / 24, readableFloor: 0.7 },
            strengths: ["Fast page loads", "Clean mobile experience", "Consistent NAP across listings"],
            weaknesses: ["No schema markup on service pages", "Thin content on the pricing page", "Missing alt text on 60% of images"],
            competitors: [
                { name: "Reform Studio KL", domain: "reformstudio.demo", aeo: 52, seo: 61, queries: 9, note: "Named more often for \"reformer pilates near me\"" },
                { name: "Core Collective", domain: "corecollective.demo", aeo: 33, seo: null, queries: 4, note: "Weaker AI visibility" },
                // A rival the scan kept but could not count (`queries: null`).
                { name: "Kinetic Pilates Puchong", domain: "kineticpilates.demo", aeo: null, seo: null, queries: null, note: "" },
            ],
            // Always sent by the real build_business_audit(); populated here so
            // the fixture exercises the same code paths as a live business.
            priorityPages: [
                { url: "https://mockbiz-scored.demo/classes", title: "Classes", visits: 412, note: "Top pages by traffic" },
                { url: "https://mockbiz-scored.demo/pricing", title: "Pricing", visits: 208, note: "Top pages by traffic" },
            ],
            underperforming: [
                { url: "https://mockbiz-scored.demo/promo-2024", title: "Promo 2024", reason: "Thin content", visits: 3 },
                { url: "https://mockbiz-scored.demo/old-timetable", title: "Old timetable", reason: "404", visits: 0 },
            ],
            recommendations: [
                { id: "rec_no_schema", title: "Add LocalBusiness and FAQ structured data", impact: "Help AI assistants and search engines understand and cite your pages.", priority: "high", effort: "Medium", horizon: "Long term" },
                { id: "rec_thin_content", title: "Expand the thin service pages", impact: "Give assistants something worth quoting about what you offer.", priority: "medium", effort: "Medium", horizon: "Long term" },
                { id: "rec_missing_alt", title: "Add alt text to product images", impact: "Recover image-search signals and improve accessibility.", priority: "low", effort: "Easy", horizon: "Quick win" },
            ],
        },
        audit: {
            checks: [
                { key: "no_schema", label: "Missing schema markup", severity: "medium", count: 8, desc: "8 pages ship no structured data" },
                { key: "missing_alt", label: "Missing alt text", severity: "easy", count: 14, desc: "14 images have no alt attribute" },
                { key: "thin_content", label: "Thin content", severity: "hard", count: 2, desc: "2 pages under 150 words" },
            ],
            pagesCrawled: 24,
        },
        // A PageSpeed block as `run_pagespeed()` sends one when the desktop
        // call failed: the live path then previews a phone-only card, Chrome's
        // "not enough real-visitor data" and a key page PSI could not score.
        pageSpeed: {
            capturedAt: "2026-07-20T04:00:00Z",
            runs: [
                {
                    strategy: "mobile",
                    performance: 47,
                    lcpMs: 4300,
                    fcpMs: 2100,
                    cls: 0.12,
                    tbtMs: 480,
                    siMs: 5200,
                    // Field data: nothing from CrUX on a site this small.
                    inpMs: null,
                    ttfbMs: 900,
                    ttiMs: 6800,
                    fieldData: "insufficient",
                    fieldPass: false,
                    opportunities: [
                        { id: "unused-javascript", label: "Reduce unused JavaScript", savingMs: 1800 },
                        { id: "uses-responsive-images", label: "Properly size images", savingMs: 1200 },
                        { id: "render-blocking-resources", label: "Eliminate render-blocking resources", savingMs: 600 },
                    ],
                    opportunitiesTotal: 7,
                    accessibility: 88,
                    bestPractices: 79,
                    seo: 92,
                    capturedAt: "2026-07-20T04:00:00Z",
                    samples: 3,
                },
            ],
            pages: [
                { url: "https://mockbiz-scored.demo/classes", title: "Classes", performance: 52, lcpMs: 3900 },
                { url: "https://mockbiz-scored.demo/pricing", title: "Pricing", performance: null, lcpMs: null },
            ],
            pagesCapturedAt: "2026-07-20T04:00:00Z",
            pagesTotal: 2,
        },
    },
    // A scan whose crawl never ran, as `run_site_audit`'s failure stub and
    // `build_business_audit` send it: AI visibility measured, the site not.
    // The block still carries zeros (every check at 0, a 0% healthByArea)
    // that nobody measured, which the live path must not read as findings.
    "mock-list-crawl-failed": {
        businessAudit: {
            siteHealthScore: WIRE_NULL,
            siteHealthVerdict: WIRE_NULL,
            siteHealthScoreAbsent: "crawl_failed",
            siteHealthReadablePages: 0,
            aiReadabilityScore: null,
            aiReadabilityVerdict: null,
            aiReadabilityAbsent: "crawl_did_not_run",
            aiReadabilityComponents: [],
            aeoScore: 36,
            aeoVerdict: "Weak AI visibility, high upside",
            // `_strengths` lists every check that found nothing, and on a crawl
            // that read nothing that is every check.
            strengths: [
                "No broken (4xx) pages – every linked URL resolves.",
                "No orphan pages – everything in the sitemap is internally linked.",
                "Every page ships structured data for AI and search engines.",
                "robots.txt is present and points crawlers at the sitemap.",
                "Every page has a meta description.",
            ],
            weaknesses: [],
            competitors: [],
            priorityPages: [],
            underperforming: [],
            recommendations: [],
        },
        audit: {
            checks: [
                { key: "broken_4xx", label: "Broken pages (4xx)", severity: "error", count: 0, desc: "Pages returning a client error waste crawl budget and frustrate visitors.", pages: [] },
                { key: "no_schema", label: "Pages missing structured data", severity: "warning", count: 0, desc: "No LocalBusiness, FAQPage or Organization markup for engines to cite.", pages: [] },
                { key: "missing_meta", label: "Missing meta descriptions", severity: "warning", count: 0, desc: "Search and AI previews fall back to arbitrary page text.", pages: [] },
            ],
            siteHealth: null,
            pagesCrawled: 0,
            errors: 0,
            warnings: 0,
            healthByArea: { crawlability: 0, internalLinking: 0, markup: "missing", robotsTxt: "not_found" },
            mode: "visibility_only",
            mode_reason: "mockbiz-unreachable.demo did not respond while we were reading it, so the website section of this report could not be completed. Your AI visibility results below are unaffected.",
            generated_at: "2026-07-22T03:40:00Z",
        },
    },
};

/**
 * A business another sample store created (a signed client's first AI check,
 * a prospect's pitch) has a finished, scored scan on its row once that scan
 * is done. The detail then carries the scored block the server returns for
 * it, so every tab reads the same scan the list does: scored, never "Not yet
 * scored" beside a row with numbers (Angel, 15 Oct). Undefined while the row
 * has no finished scan.
 */
function registeredResults(row: AeoBusiness): NonNullable<AeoBusinessDetail["latest_assessment"]>["results"] | undefined {
    const la = row.latest_assessment;
    if (!REGISTERED_BUSINESSES.some((r) => r.id === row.id) || !la || la.gated) return undefined;
    if (la.status !== "complete" && la.status !== "partial") return undefined;
    const aeo = la.aeo_score ?? null;
    const site = la.seo_score ?? null;
    const band = (n: number | null, what: string) =>
        n === null ? null : n >= 70 ? `Strong ${what}` : n >= 45 ? `Developing ${what}, clear upside` : `Weak ${what}, high upside`;
    return {
        businessAudit: {
            siteHealthScore: site,
            siteHealthVerdict: band(site, "SEO fundamentals"),
            aeoScore: aeo,
            aeoVerdict: band(aeo, "AI visibility"),
            strengths: [],
            weaknesses: [],
            competitors: [],
            priorityPages: [],
            underperforming: [],
            recommendations: [],
        },
    } as unknown as NonNullable<AeoBusinessDetail["latest_assessment"]>["results"];
}

/** GET /api/v1/businesses/{id} fixture — reuses the same combined list
 *  listBusinesses() builds, so a business found in the table is always
 *  resolvable here too (no dead links from the demo table). */
export function getBusinessDetail(id: string): Promise<AeoBusinessDetail | null> {
    return listBusinesses().then((all) => {
        const found = all.find((b) => b.id === id || b.domain === id);
        if (!found) return null;
        const results = DEMO_AUDIT[found.id] ?? registeredResults(found);
        return {
            ...found,
            latest_assessment: found.latest_assessment
                ? { ...found.latest_assessment, results }
                : null,
        };
    });
}

const DEMO_CONNECTORS: AeoConnector[] = [
    { key: "gsc", label: "Google Search Console", category: "search", blurb: "Read impressions, organic traffic and indexed pages.", status: "connected", detail: "Connected 12 Jul · ourteam is a manager", lastSync: "2026-07-27T02:00:00Z" },
    { key: "ga4", label: "Google Analytics 4", category: "search", blurb: "Traffic and conversion data.", status: "not_connected", detail: "Not connected", lastSync: null },
    // Here so the Wix form and its three failure paths are reachable on
    // fixtures (#3691). Not connected: the mock's job is the connect flow, and
    // a demo that starts connected is a demo of the one screen nobody needs.
    { key: "wix", label: "Wix", category: "cms", blurb: "Publish fixes to a Wix site.", status: "not_connected", detail: "Not connected", lastSync: null },
    // No Google Business Profile: the BE catalogue dropped it (#3801).
    // Status only (documents phase 1): the Connectors screen reads this row's
    // state from the documents sample store, per business, so the status
    // here is only the catalog's shape.
    { key: "google_docs", label: "Google Docs", category: "docs", blurb: "Reads the client's docs and the comments on them.", status: "not_connected", detail: "Runs as oura@ourteam.ai", lastSync: null },
];

/** GET /api/v1/businesses/{id}/connectors fixture — same three-connector demo
 *  state for every business id, since the real catalog is business-agnostic.
 *  Keeps the `id` param for interface parity with the real function even
 *  though this mock ignores it. */
export function getBusinessConnectors(id: string): Promise<AeoConnector[]> {
    void id;
    return new Promise((resolve) => {
        window.setTimeout(() => resolve(DEMO_CONNECTORS.map((c) => ({ ...c }))), 150);
    });
}

// ─── Remediation fixtures (ourteam-backend#3804) ─────────────────────────────
// Enough to exercise every branch the drawer renders: a publishable draft, a
// paste-it-yourself one, a published change that can be reverted, and one that
// failed. Kept in module state so a publish in the UI is visible in the list
// immediately, the way the live endpoints behave.

const FIXTURE_DRAFTS: AeoDraft[] = [
    {
        id: "draft-meta-1",
        businessDomain: "pilatescollective.demo",
        trackerTaskId: "#13",
        pageUrl: "https://pilatescollective.demo/classes",
        issueKey: "meta_description_missing",
        draftType: "meta_description",
        draft: "Reformer and mat Pilates classes in Mont Kiara for every level, with small "
            + "group sessions capped at eight. Book a free intro class this week.",
        rationale: "The page had no meta description, so Google was writing its own from the "
            + "first sentence of the timetable. This states what the studio offers, where it "
            + "is, and what to do next, in 155 characters.",
        guidance: {},
        status: "draft",
        publishable: true,
        createdBy: "oura-staff",
        createdAt: "2026-07-28T09:14:00Z",
    },
    {
        id: "draft-schema-1",
        businessDomain: "pilatescollective.demo",
        trackerTaskId: "#14",
        pageUrl: "https://pilatescollective.demo/",
        issueKey: "no_schema",
        draftType: "local_business_schema",
        draft: '{"@context":"https://schema.org","@type":"ExerciseGym",'
            + '"name":"Pilates Collective","address":{"@type":"PostalAddress",'
            + '"addressLocality":"Mont Kiara","addressRegion":"Kuala Lumpur"}}',
        rationale: "No structured data anywhere on the site, so assistants have nothing "
            + "authoritative to read the studio's name, address or opening hours from.",
        guidance: {
            steps: [
                "Open the site's theme header template.",
                "Paste the JSON-LD block inside <head>, before the closing tag.",
                "Re-run the audit to confirm it validates.",
            ],
        },
        status: "draft",
        publishable: false,
        createdBy: "oura-staff",
        createdAt: "2026-07-28T09:16:00Z",
    },
];

const FIXTURE_PUBLICATIONS: AeoPublication[] = [
    {
        id: "pub-1",
        draftId: "draft-title-1",
        businessDomain: "pilatescollective.demo",
        connector: "wordpress",
        target: "https://pilatescollective.demo/pricing",
        status: "published",
        error: null,
        approvedBy: "jovi@ourteam.ai",
        publishedAt: "2026-07-27T11:02:00Z",
        revertedBy: null,
        revertedAt: null,
        reversible: true,
    },
    {
        id: "pub-2",
        draftId: "draft-alt-1",
        businessDomain: "pilatescollective.demo",
        connector: "wordpress",
        status: "failed",
        target: null,
        error: "WordPress rejected the application password (401).",
        approvedBy: "jovi@ourteam.ai",
        publishedAt: null,
        revertedBy: null,
        revertedAt: null,
        reversible: false,
    },
];

const draftStore = [...FIXTURE_DRAFTS];
const publicationStore = [...FIXTURE_PUBLICATIONS];

export function listDrafts(_businessId: string, trackerTaskId?: string): Promise<AeoDraft[]> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            resolve(trackerTaskId
                ? draftStore.filter((d) => d.trackerTaskId === trackerTaskId)
                : [...draftStore]);
        }, 220);
    });
}

export function createDraft(
    _businessId: string,
    input: AeoDraftCreateRequest,
): Promise<AeoDraft | null> {
    return new Promise((resolve) => {
        // Slow on purpose: generating a draft is an LLM round-trip on the real
        // endpoint, and a drawer that returns instantly hides the loading state
        // we need to have designed.
        window.setTimeout(() => {
            const draft: AeoDraft = {
                id: `draft-${draftStore.length + 1}`,
                businessDomain: "pilatescollective.demo",
                trackerTaskId: input.tracker_task_id ?? null,
                pageUrl: input.page_url,
                issueKey: input.issue_key,
                draftType: input.draft_type,
                draft: input.unanswered_questions?.length
                    ? `Draft page answering: ${input.unanswered_questions.slice(0, 3).join("; ")}`
                    : "Generated draft copy for this page and issue.",
                rationale: "Generated from the audit finding and the page's existing copy.",
                guidance: {},
                status: "draft",
                publishable: input.draft_type !== "local_business_schema",
                createdBy: "oura-staff",
                createdAt: new Date().toISOString(),
            };
            draftStore.unshift(draft);
            resolve(draft);
        }, 1400);
    });
}

export function previewDraft(draftId: string): Promise<Record<string, unknown> | null> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            const draft = draftStore.find((d) => d.id === draftId);
            resolve(draft
                ? { before: "(no meta description on this page)", after: draft.draft }
                : null);
        }, 260);
    });
}

export function publishDraft(
    draftId: string,
): Promise<{ ok: true; publication: AeoPublication } | { ok: false; message: string }> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            const draft = draftStore.find((d) => d.id === draftId);
            if (!draft) return resolve({ ok: false, message: "Draft not found" });
            if (!draft.publishable) {
                // Mirrors the BE: a type we cannot write is refused rather than
                // half-attempted, so the UI has to offer the manual path.
                return resolve({
                    ok: false,
                    message: "This change can't be published automatically — follow the steps to apply it.",
                });
            }
            const publication: AeoPublication = {
                id: `pub-${publicationStore.length + 1}`,
                draftId: draft.id,
                businessDomain: draft.businessDomain,
                connector: "wordpress",
                target: draft.pageUrl,
                status: "published",
                error: null,
                approvedBy: "you@ourteam.ai",
                publishedAt: new Date().toISOString(),
                revertedBy: null,
                revertedAt: null,
                reversible: true,
            };
            publicationStore.unshift(publication);
            draft.status = "published";
            resolve({ ok: true, publication });
        }, 900);
    });
}

export function listPublications(): Promise<AeoPublication[]> {
    return new Promise((resolve) => {
        window.setTimeout(() => resolve([...publicationStore]), 220);
    });
}

export function revertPublication(publicationId: string): Promise<boolean> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            const pub = publicationStore.find((p) => p.id === publicationId);
            if (!pub || !pub.reversible) return resolve(false);
            pub.status = "reverted";
            pub.revertedBy = "you@ourteam.ai";
            pub.revertedAt = new Date().toISOString();
            pub.reversible = false;
            resolve(true);
        }, 700);
    });
}

// ─── Tracker fixtures (ourteam-backend#3686) ─────────────────────────────────
// A four-week plan with enough shape to exercise the board, the timeline and
// the scoreboard: tasks in every status, both owners, and a scoreboard row that
// has NOT been re-measured yet (result: null) so the amber "pending" cell is
// reachable without waiting a month for a real re-scan.

const trackerStore: AeoTracker = {
    reference: "OURA-DEMO-001",
    packageName: "AI Visibility Sprint",
    window: "29 Jul – 26 Aug 2026",
    currentWeek: 2,
    nextDeliverable: "Structured data live on every service page",
    scoreboard: [
        { key: "aeo", label: "AI visibility score", baseline: "41 / 100", target: "60 / 100", result: null, note: "Re-measured at the end of week 4." },
        { key: "site_health", label: "Site health", baseline: "58 / 100", target: "80 / 100", result: "71 / 100", note: "Schema and meta descriptions landed in week 1." },
        { key: "named_rate", label: "How often AI names you", baseline: "8%", target: "25%", result: null, note: "Needs a full re-scan to move." },
    ],
    weeks: [
        {
            week: 1, title: "Foundations", goal: "Make the site machine-readable.",
            deliverable: "Schema and meta descriptions on every page", dates: "29 Jul – 4 Aug",
            tasks: [
                { id: "demo:w1:1", action: "Add LocalBusiness schema sitewide", notes: "Homepage and each location page.", tool: "WordPress", gate: "Rich Results Test returns a LocalBusiness item", owner: "ourteam", status: "done", auto: false, source: { type: "check", ref: "no_schema" } },
                { id: "demo:w1:2", action: "Write meta descriptions for the 9 pages missing them", notes: "", tool: "WordPress", gate: "Re-crawl reports 0 pages missing meta", owner: "ourteam", status: "done", auto: false, source: { type: "check", ref: "missing_meta" } },
                { id: "demo:w1:3", action: "Re-run the site audit", notes: "Confirms week 1 landed.", tool: "Oura", gate: "Audit shows the two checks cleared", owner: "ourteam", status: "done", auto: true, source: { type: "manual", ref: "" } },
            ],
        },
        {
            week: 2, title: "Content", goal: "Answer the questions customers actually ask.",
            deliverable: "Three pages answering the top unanswered questions", dates: "5 – 11 Aug",
            tasks: [
                { id: "demo:w2:1", action: "Write a page answering \"is chiropractic safe during pregnancy\"", notes: "Top unanswered question by impressions.", tool: "WordPress", gate: "Page live and answering the query", owner: "ourteam", status: "in_progress", auto: false, source: { type: "query", ref: "is chiropractic safe during pregnancy" } },
                { id: "demo:w2:2", action: "Expand the three thin event pages past 300 words", notes: "Currently ~100 words each.", tool: "WordPress", gate: "Re-crawl reports 0 thin pages", owner: "ourteam", status: "not_started", auto: false, source: { type: "check", ref: "thin_content" } },
                // A contradiction finding, as a normal tracker task (#3760 /
                // ourteam-backend#4837). Here so the evidence block is reviewable
                // without the contradiction model having run: both quotes, the
                // engine, and the citation K-1 verified. `stale` is true on
                // purpose — the caution is the half of the card that otherwise
                // never gets looked at.
                { id: "demo:w2:4", action: "Correct what assistants say about: opening hours", notes: "An assistant said: \u201copen until 6pm\u201d Their own document says: \u201cWeekdays 9am to 9pm\u201d", tool: "cms", gate: "Done and checked live", owner: "ourteam", status: "not_started", auto: false, source: { type: "rule", ref: "document_contradiction" }, templateKey: "contradiction", evidence: { question: "What time does the clinic close?", assistantQuote: "It is a KL clinic offering facials and massage services, open until 6pm.", documentQuote: "Weekdays 9am to 9pm. We do not offer massage.", engine: "openai", answeredAt: "2026-09-11T04:20:00Z", assessmentId: "demo-assessment", stale: true, citation: { source_id: "src_demo_pricelist", source_title: "Price list 2025.pdf", label: "prices", content_as_of: "2025-02-03", quote: "Weekdays 9am to 9pm. We do not offer massage.", start: 412, end: 456 } } },
                { id: "demo:w2:3", action: "Supply clinic pricing for the pricing page", notes: "Blocked pending the client's confirmed rates.", tool: "—", gate: "Pricing published", owner: "client", status: "blocked", auto: false, source: { type: "manual", ref: "" } },
            ],
        },
        {
            week: 3, title: "Reputation", goal: "Get named on sources AI already trusts.",
            deliverable: "Listings live on the local directories that matter", dates: "12 – 18 Aug",
            tasks: [
                { id: "demo:w3:1", action: "Claim and complete the top 5 local directory listings", notes: "", tool: "—", gate: "All five live with matching NAP", owner: "ourteam", status: "not_started", auto: false, source: { type: "manual", ref: "" } },
                { id: "demo:w3:2", action: "Request reviews from the last 20 patients", notes: "Client sends; we supply the template.", tool: "—", gate: "10+ new reviews", owner: "client", status: "not_started", auto: false, source: { type: "manual", ref: "" } },
            ],
        },
        {
            week: 4, title: "Re-measure", goal: "Prove the work moved the number.",
            deliverable: "Fresh assessment and the month's report", dates: "19 – 26 Aug",
            tasks: [
                { id: "demo:w4:1", action: "Re-run the full assessment", notes: "", tool: "Oura", gate: "New scores captured", owner: "ourteam", status: "not_started", auto: true, source: { type: "manual", ref: "" } },
                { id: "demo:w4:2", action: "Send the client report", notes: "", tool: "Oura", gate: "Report delivered", owner: "ourteam", status: "not_started", auto: false, source: { type: "manual", ref: "" } },
            ],
        },
    ],
    fixRegister: [
        { issueKey: "no_schema", issue: "No structured data anywhere on the site", detectedWhere: "24 pages", fixedAt: "Week 1", status: "done", note: "LocalBusiness + FAQ added." },
        { issueKey: "missing_meta", issue: "Pages without meta descriptions", detectedWhere: "9 pages", fixedAt: "Week 1", status: "done", note: "" },
        { issueKey: "thin_content", issue: "Thin content pages", detectedWhere: "7 pages", fixedAt: "Week 2", status: "pending", note: "" },
        { issueKey: "backlinks", issue: "Almost no independent mentions", detectedWhere: "Off-site", fixedAt: "Week 3", status: "pending", note: "" },
    ],
};

export function getTracker(): Promise<AeoTracker | null> {
    return new Promise((resolve) => {
        // Structured-clone so a caller mutating the returned object cannot edit
        // the fixture in place, which is the real endpoint's behaviour too.
        window.setTimeout(() => resolve(JSON.parse(JSON.stringify(trackerStore))), 250);
    });
}

/**
 * GET /tracker/summary against the fixture plan: the fields the route reads
 * off the stored plan (id, action, week, status, source), so a screen that
 * asks the summary first finds the plan `getTracker` above serves.
 * fixture-only: the preview has one plan, whichever assessment asks.
 */
export function getTrackerSummary(): Promise<AeoTrackerSummary> {
    const tasks = trackerStore.weeks.flatMap((w) =>
        w.tasks.map((t) => ({
            id: t.id,
            action: t.action,
            week: w.week,
            status: t.status,
            source: t.source ?? { type: "manual", ref: null },
        })),
    );
    return new Promise((resolve) => {
        window.setTimeout(() => resolve({ exists: true, tasks }), 150);
    });
}

export function patchTrackerTask(
    taskId: string,
    patch: AeoTrackerTaskPatch,
): Promise<AeoTracker | null> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            for (const week of trackerStore.weeks) {
                const idx = week.tasks.findIndex((t) => t.id === taskId);
                if (idx === -1) continue;
                const task = week.tasks[idx];
                if (patch.removed) {
                    week.tasks.splice(idx, 1);
                } else if (patch.week && patch.week !== week.week) {
                    week.tasks.splice(idx, 1);
                    trackerStore.weeks.find((w) => w.week === patch.week)?.tasks.push(task);
                } else if (patch.status) {
                    task.status = patch.status;
                }
                break;
            }
            resolve(JSON.parse(JSON.stringify(trackerStore)));
        }, 200);
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// Knowledge sources (ourteam-frontend#3684 / ourteam-backend#4749)
//
// The backend ticket is not shipped, so this is the only way either surface can
// be exercised. It is written to be a fair rehearsal rather than a happy path:
// the states that are hard to reach on purpose — a file still being read, a
// scanned PDF, a duplicate — are the ones the screens most need to get right.
//
// Steered by the filename, so a reviewer reaches each without editing code:
//
//   contains "scan"  → status failed, with the backend's own sentence
//   contains "dup"   → 409, the duplicate row
//   contains "slow"  → stays `pending` for 8s instead of 2.5s
//   anything else    → `pending`, then `ready` on its own
// ─────────────────────────────────────────────────────────────────────────────

/** Keyed by business id AND by domain — staged rows arrive before a business
 *  exists, and `listKnowledge` folds them in once one does. */
const knowledgeStore: Record<string, AeoKnowledgeSource[]> = {
    "nyonya-table": [
        {
            id: "kn-nyonya-1", kind: "text",
            title: "Goals and plans (onboarding form)", label: "note", charCount: 148,
            status: "ready", addedByRole: "oura-client", createdAt: "2026-10-19T06:00:00Z",
        },
        {
            id: "kn-nyonya-2", kind: "text",
            title: "Customer questions (onboarding form)", label: "faq", charCount: 889,
            status: "ready", addedByRole: "oura-client", createdAt: "2026-10-19T06:00:00Z",
        },
    ],
    "pilates-collective": [
        {
            id: "kn-seed-1", kind: "file",
            title: "Reformer price list 2026.pdf", label: "prices", contentAsOf: "2026-07-01",
            mime: "application/pdf", sizeBytes: 184_320, charCount: 3_180,
            status: "ready", addedBy: "Wei Ling", addedByRole: "oura-staff",
            createdAt: "2026-08-14T03:22:00Z",
        },
        {
            id: "kn-seed-2", kind: "text",
            title: "Opening hours (from owner's WhatsApp)", label: "note",
            contentAsOf: "2026-09-02", charCount: 412,
            status: "ready", addedBy: "Wei Ling", addedByRole: "oura-staff",
            createdAt: "2026-09-02T08:10:00Z",
        },
        {
            id: "kn-seed-3", kind: "file",
            title: "Studio brochure (2024).pdf", label: "brochure",
            // Deliberately old: this is the row that proves the staleness badge
            // renders, which is the whole reason content_as_of is its own field.
            contentAsOf: "2024-05-01",
            mime: "application/pdf", sizeBytes: 2_411_008, charCount: 9_540,
            status: "ready", addedBy: "Wei Ling", addedByRole: "oura-staff",
            createdAt: "2026-06-30T01:00:00Z",
        },
    ],
};

function knowledgeDomainFor(businessId: string): string | null {
    const all = [...FIXTURE_BUSINESSES, ...DEMO_BUSINESSES];
    return all.find((b) => b.id === businessId)?.domain ?? null;
}

/**
 * What is inside each source, keyed by source id: the note's own text, or the
 * text "read" from a file. A file added this session also keeps a local link to
 * its bytes, so the reader shows the real PDF.
 */
const knowledgeText: Record<string, string> = {
    "kn-seed-2": "Mon to Fri 7am to 9pm\nSat and Sun 8am to 2pm\nClosed on public holidays.\n\nThe owner says the 7am class is the busiest, book a day ahead.",
    "kn-seed-1": "Reformer price list 2026\n\nSingle class RM 85\n5 class pack RM 400 (valid 2 months)\n10 class pack RM 750 (valid 4 months)\nPrivate session RM 220\n\nPrices include SST. Packs are not shareable.",
    "kn-seed-3": "The Pilates Collective\n\nA boutique reformer studio in Bangsar, founded 2019.\nSmall classes of 8, every instructor certified.\n\n(This brochure is from 2024. Some prices have changed.)",
    "kn-nyonya-1": "Goals:\n- 1 - Increase bookings or enquiries\n- 2 - Increase online sales\n- 3 - Improve visibility in Google Search and AI-generated answers\n\nUpcoming plans:\nOpening a second outlet in Jonker Street in March 2027. Want the website ready for catering packages before then.\n\nAnything else:\nWe get a lot of enquiries through Instagram DMs too, not just WhatsApp.",
    "kn-nyonya-2": "- These are the top 5 from the last 90 days of chats:\n- **Do you take reservations?** Yes, for groups of 6 or more. Weekends fill up by Thursday.\n- **Is the food halal?** Pork free and no lard, but not halal certified. Muslim guests ask this the most.\n- **Where do we park?** Free parking behind the shop after 6pm, paid street parking before that.\n- **Do you cater for events?** Yes, minimum 30 pax with 3 days notice. Corporates ask for a quotation first.\n- **What is the signature dish?** Ayam pongteh and the nyonya laksa, both sell out by 8pm.\n- Close behind are birthday cakes, private rooms and delivery areas.",
};
const knowledgeFileUrl: Record<string, string> = {};

let knowledgeSeq = 100;
const knowledgeId = () => `kn-${++knowledgeSeq}`;

function knowledgeDelay<T>(value: T, ms = 250): Promise<T> {
    return new Promise((resolve) => window.setTimeout(() => resolve(value), ms));
}

/**
 * The ingestion the backend does inline: the row exists immediately as
 * `pending`, and becomes `ready` (or `failed`) on its own. The screens poll,
 * so flipping it here in a timer is what proves the poll works.
 */
function knowledgeIngest(key: string, source: AeoKnowledgeSource, filename: string): void {
    const slow = /slow/i.test(filename);
    window.setTimeout(() => {
        const row = (knowledgeStore[key] ?? []).find((s) => s.id === source.id);
        if (!row) return;
        if (/scan/i.test(filename)) {
            row.status = "failed";
            row.errorDetail =
                "This PDF is a scan. We could not read any text from it. Send the original file, or paste the text.";
            return;
        }
        row.status = "ready";
        row.charCount = 1_200 + Math.floor(Math.random() * 8_000);
    }, slow ? 8_000 : 2_500);
}

function knowledgeAddFile(
    key: string, domain: string, file: File, opts?: { label?: string; contentAsOf?: string },
): Promise<KnowledgeAddResult> {
    if (/dup/i.test(file.name)) {
        return knowledgeDelay<KnowledgeAddResult>({
            ok: false, kind: "duplicate", title: file.name,
            message: "Same file, same contents, added a moment ago.",
        }, 400);
    }
    const source: AeoKnowledgeSource = {
        id: knowledgeId(), kind: "file",
        title: file.name, label: opts?.label ?? null,
        contentAsOf: opts?.contentAsOf ?? new Date().toISOString().slice(0, 10),
        mime: file.type || "application/octet-stream", sizeBytes: file.size,
        charCount: null, status: "pending", addedBy: "You", addedByRole: "oura-staff",
        createdAt: new Date().toISOString(),
    };
    knowledgeStore[key] = [source, ...(knowledgeStore[key] ?? [])];
    knowledgeFileUrl[source.id] = URL.createObjectURL(file);
    if (/^text\/|\.(txt|md|csv)$/i.test(file.type || file.name)) void file.text().then((t) => { knowledgeText[source.id] = t; });
    else knowledgeText[source.id] = `Text Oura read from ${file.name}.`;
    knowledgeIngest(key, source, file.name);
    return knowledgeDelay<KnowledgeAddResult>({ ok: true, sources: [source] }, 400);
}

function knowledgeAddText(
    key: string, domain: string, input: { title: string; text: string; label?: string; content_as_of?: string },
): Promise<KnowledgeAddResult> {
    const source: AeoKnowledgeSource = {
        id: knowledgeId(), kind: "text",
        title: input.title, label: input.label ?? "note",
        contentAsOf: input.content_as_of ?? new Date().toISOString().slice(0, 10),
        charCount: input.text.length,
        // Text needs no extraction, so it is ready the moment it is stored —
        // the same reason the backend writes it `ready` in one step.
        status: "ready", addedBy: "You", addedByRole: "oura-staff",
        createdAt: new Date().toISOString(),
    };
    knowledgeStore[key] = [source, ...(knowledgeStore[key] ?? [])];
    knowledgeText[source.id] = input.text;
    return knowledgeDelay<KnowledgeAddResult>({ ok: true, sources: [source] }, 300);
}

export function listKnowledge(businessId: string): Promise<KnowledgePage> {
    const own = knowledgeStore[businessId] ?? [];
    // Re-parenting, mocked: the staged rows are already keyed on the domain, so
    // folding them in on read is the same outcome the server reaches without
    // needing a hook into the scan flow, which the mock does not own.
    const domain = knowledgeDomainFor(businessId);
    const staged = domain ? knowledgeStore[domain] ?? [] : [];
    return knowledgeDelay<KnowledgePage>(knowledgePage([...own, ...staged]), 200);
}

/**
 * The `{ sources, limits }` envelope the server sends, so the fixture path
 * exercises the budget line rather than leaving it untested until production.
 */
function knowledgePage(rows: AeoKnowledgeSource[]): KnowledgePage {
    const sources = rows.map((s) => ({ ...s }));
    return {
        sources,
        limits: {
            maxSources: 25,
            maxFileBytes: 20 * 1024 * 1024,
            maxTotalChars: 2_000_000,
            usedSources: sources.length,
            usedChars: sources.reduce((n, s) => n + (s.charCount ?? 0), 0),
            labels: ["menu", "prices", "faq", "bios", "brochure", "policy", "note", "other"],
        },
    };
}

/** New scan, before the business exists: the rows staged under the domain. */
export function listStagedKnowledge(domain: string): Promise<KnowledgePage> {
    return knowledgeDelay<KnowledgePage>(knowledgePage(knowledgeStore[domain] ?? []), 200);
}

export function deleteStagedKnowledge(sourceId: string, domain: string): Promise<StagedDeleteResult> {
    const before = (knowledgeStore[domain] ?? []).length;
    knowledgeStore[domain] = (knowledgeStore[domain] ?? []).filter((s) => s.id !== sourceId);
    const removed = knowledgeStore[domain].length < before;
    return knowledgeDelay<StagedDeleteResult>(
        removed ? { ok: true } : { ok: false, message: "Knowledge source not found" },
        200,
    );
}

export function uploadKnowledgeFile(
    businessId: string, file: File, opts?: { label?: string; contentAsOf?: string },
): Promise<KnowledgeAddResult> {
    return knowledgeAddFile(businessId, knowledgeDomainFor(businessId) ?? businessId, file, opts);
}

export function addKnowledgeText(
    businessId: string, input: { title: string; text: string; label?: string; content_as_of?: string },
): Promise<KnowledgeAddResult> {
    return knowledgeAddText(businessId, knowledgeDomainFor(businessId) ?? businessId, input);
}

export function stageKnowledgeFile(
    domain: string, file: File, opts?: { label?: string; contentAsOf?: string },
): Promise<KnowledgeAddResult> {
    return knowledgeAddFile(domain, domain, file, opts);
}

export function stageKnowledgeText(
    domain: string, input: { title: string; text: string; label?: string; content_as_of?: string },
): Promise<KnowledgeAddResult> {
    return knowledgeAddText(domain, domain, input);
}

export function patchKnowledge(
    businessId: string, sourceId: string,
    patch: { title?: string; label?: string; content_as_of?: string; status?: "ready" | "excluded" },
): Promise<AeoKnowledgeSource | null> {
    const domain = knowledgeDomainFor(businessId);
    for (const key of [businessId, domain].filter(Boolean) as string[]) {
        const row = (knowledgeStore[key] ?? []).find((s) => s.id === sourceId);
        if (row) {
            Object.assign(row, patch);
            return knowledgeDelay({ ...row }, 200);
        }
    }
    return knowledgeDelay(null, 200);
}

export function getKnowledgeContent(_businessId: string, sourceId: string): Promise<KnowledgeContent | null> {
    const text = knowledgeText[sourceId] ?? null;
    const fileUrl = knowledgeFileUrl[sourceId] ?? null;
    return knowledgeDelay(text === null && fileUrl === null ? null : { text, fileUrl }, 300);
}

export function deleteKnowledge(businessId: string, sourceId: string): Promise<boolean> {
    const domain = knowledgeDomainFor(businessId);
    let removed = false;
    for (const key of [businessId, domain].filter(Boolean) as string[]) {
        const before = (knowledgeStore[key] ?? []).length;
        knowledgeStore[key] = (knowledgeStore[key] ?? []).filter((s) => s.id !== sourceId);
        if (knowledgeStore[key].length < before) removed = true;
    }
    return knowledgeDelay(removed, 200);
}

/**
 * Connecting, against fixtures (ourteam-frontend#3691).
 *
 * The real call reaches out to a client's account, so there is nothing to point
 * it at here. What the mock is for is the three outcomes the Wix form has to
 * handle and that are otherwise unreachable without a Wix account: a clean
 * connect, a key missing the SEO scope, and an account holding two sites on the
 * same domain.
 *
 * Steered by what is typed, so a reviewer can reach each one without editing
 * code — the account ID decides:
 *
 *   contains "multi"    → wix_multiple_matching_sites, two candidates
 *   contains "scope"    → wix_scope_missing
 *   contains "nosite"   → wix_no_matching_site
 *   contains "timeout"  → wix_timeout
 *   an API key with "bad" → wix_auth_failed
 *   anything else       → connected
 *
 * A second submit carrying `site_id` always connects: that is the picker's
 * whole path, and it is the half most likely to rot unnoticed.
 */
export function connectConnector(
    key: string,
    payload: { api_key?: string; account_id?: string; site_id?: string },
): Promise<
    | { ok: true; kind: "connected"; detail: string }
    | { ok: false; message: string; code?: string; sites?: Array<{ site_id: string; site_display_name?: string; domain?: string }> }
> {
    const settle = <T,>(value: T): Promise<T> =>
        new Promise((resolve) => window.setTimeout(() => resolve(value), 400));

    if (key !== "wix") {
        return settle({
            ok: false as const,
            message: "Connecting is disabled while Oura is serving fixture data.",
        });
    }

    const account = (payload.account_id ?? "").toLowerCase();
    const apiKey = (payload.api_key ?? "").toLowerCase();

    if (payload.site_id) {
        return settle({
            ok: true as const,
            kind: "connected" as const,
            detail: "Connected to The Lush Clinic on Wix.",
        });
    }
    if (apiKey.includes("bad")) {
        return settle({
            ok: false as const,
            message: "Wix rejected that API key.",
            code: "wix_auth_failed",
        });
    }
    if (account.includes("multi")) {
        return settle({
            ok: false as const,
            message: "This account has more than one site on that domain.",
            code: "wix_multiple_matching_sites",
            sites: [
                { site_id: "11111111-1111-1111-1111-111111111111", site_display_name: "The Lush Clinic", domain: "thelushclinic.com" },
                { site_id: "22222222-2222-2222-2222-222222222222", site_display_name: "Lush Clinic (old)", domain: "thelushclinic.com" },
            ],
        });
    }
    if (account.includes("scope")) {
        return settle({
            ok: false as const,
            message: "That key does not carry the SEO permission.",
            code: "wix_scope_missing",
        });
    }
    if (account.includes("nosite")) {
        return settle({
            ok: false as const,
            message: "No site on that account serves this business's domain.",
            code: "wix_no_matching_site",
        });
    }
    if (account.includes("timeout")) {
        return settle({
            ok: false as const,
            message: "Wix did not answer in time.",
            code: "wix_timeout",
        });
    }
    return settle({
        ok: true as const,
        kind: "connected" as const,
        detail: "Connected to The Lush Clinic on Wix.",
    });
}

// ─── AI Readability refresh (#3831) ──────────────────────────────────────────
//
// The fixture business has a readability block already, so the mock answers the
// cheap mode: a refresh returns the same numbers with a fresh `measuredAt`.
// Inventing a movement here would train an operator to expect one.

const readabilityJobs: Record<string, AeoReadabilityRefreshJob> = {};

export function startReadabilityRefresh(businessId: string): Promise<AeoReadabilityRefreshStart> {
    const jobId = `demo-readability-${Date.now()}`;
    readabilityJobs[jobId] = {
        status: "done",
        mode: "recompute",
        error: null,
        aiReadability: {
            score: 62,
            verdict: "Developing AI readability, clear upside",
            reason: null,
            components: [
                { key: "headings", label: "Pages with a heading", covered: 24, applicable: 30, score: 80 },
                { key: "entity_markup", label: "Pages with entity markup", covered: 9, applicable: 30, score: 30 },
                { key: "prices", label: "Treatment pages with a price", covered: 5, applicable: 8, score: 63 },
                { key: "staff", label: "Staff pages linked to the business", covered: 4, applicable: 6, score: 67 },
                { key: "branches", label: "Branch records found", covered: 2, applicable: 3, score: 67 },
            ],
            measuredAt: new Date().toISOString(),
        },
    };
    void businessId;
    return new Promise((r) => setTimeout(() => r({ ok: true, jobId, mode: "recompute" }), 300));
}

export function getReadabilityRefresh(
    businessId: string,
    jobId: string,
): Promise<AeoReadabilityRefreshJob | "gone"> {
    void businessId;
    return new Promise((r) => setTimeout(() => r(readabilityJobs[jobId] ?? "gone"), 200));
}

/** Renaming a fixture business (ourteam-backend#5003): accepted, and forgotten
 *  on reload — the fixture store is regenerated per session. */
export function renameBusiness(businessId: string, name: string): Promise<{ ok: true }> {
    void businessId; void name;
    return new Promise((r) => setTimeout(() => r({ ok: true }), 250));
}

// ─── Top Searches (#3877-#3880, ourteam-backend#4981/#4982) ──────────────────
//
// The fixture block exists so the two screens can be exercised without a
// Search Console property. It is deliberately NOT tidy: one slot owns two
// searches, one candidate is forbidden by the recipe, one search is already
// covered, and one row ranks past 50 so it carries no tier. Those are the
// cases the screens get wrong, so they are the ones a demo must contain.

const FIXTURE_SEARCH_QUESTIONS: AeoSearchQuestions = {
    connected: true,
    window: { start: "2025-09-19", end: "2026-09-18", days: 365 },
    fetchedAt: "2026-09-19T01:00:00Z",
    matches: [
        { slot: 27, query: "peranakan restaurant bukit timah", searches: 5287, clicks: 1118, position: 2.9, rank: 4 },
        { slot: 27, query: "nyonya food near me", searches: 5238, clicks: 821, position: 2.7, rank: 5 },
        { slot: 41, query: "peranakan set menu price", searches: 5436, clicks: 172, position: 4.7, rank: 3 },
        // Ranked past the fifty, and the only search on its slot: the case that
        // used to render nothing at all, so a staffer could not tell "no search
        // reached this question" from "one did, ranked 126th".
        { slot: 12, query: "laksa opening hours today", searches: 147, clicks: 0, position: 8.62, rank: 126 },
    ],
    searches: [
        {
            query: "peranakan set menu price", rank: 3, searches: 5436, clicks: 172, position: 4.7,
            page: "https://nyonyatable.com/menu", siteAnswers: true, coveredBy: 41, candidate: null,
        },
        {
            query: "peranakan restaurant bukit timah", rank: 4, searches: 5287, clicks: 1118, position: 2.9,
            page: "https://nyonyatable.com/", siteAnswers: true, coveredBy: 27, candidate: null,
        },
        {
            query: "kueh delivery singapore", rank: 6, searches: 5226, clicks: 17, position: 35.8,
            page: "https://nyonyatable.com/", siteAnswers: false, coveredBy: null,
            candidate: { variable: "secondary_service_2", value: "kueh delivery", replaces: "catering", allowed: true, reason: null },
        },
        {
            query: "peranakan restaurant jurong", rank: 14, searches: 2867, clicks: 60, position: 10.4,
            page: "https://nyonyatable.com/locations/bukit-bintang", siteAnswers: false, coveredBy: null,
            candidate: { variable: "outlet_area", value: "Jurong", replaces: null, allowed: false,
                         reason: "No outlet in Jurong. A location question needs one, so this stays a page task." },
        },
        {
            query: "halal peranakan food", rank: 24, searches: 1650, clicks: 12, position: 18.2,
            page: null, siteAnswers: false, coveredBy: null,
            candidate: { variable: "audience_2", value: "halal diners", replaces: "families", allowed: true, reason: null },
        },
        {
            query: "nyonya table opening hours", rank: 62, searches: 210, clicks: 88, position: 1.2,
            page: "https://nyonyatable.com/", siteAnswers: true, coveredBy: null, candidate: null,
        },
        {
            // Judgement not computed. Every row of the first live scan looked
            // like this, so the demo has to carry one: it must read "not
            // measured", never a red No.
            query: "peranakan set lunch", rank: 8, searches: 5226, clicks: 3, position: 36.4,
            page: null, siteAnswers: null, siteAnswersAbsent: "no_page", coveredBy: null,
            candidate: { variable: "secondary_service_3", value: "set lunch", replaces: "kueh platter", allowed: true, reason: null },
        },
    ],
    dropped: { branded: 0, belowFloor: 11, outOfBand: 24, outOfMarket: 41 },
    // The judgement's own coverage (#5034): one row could not be judged, and the
    // reason carries the backend's sentence for it.
    siteAnswersCoverage: { total: 7, judged: 6, answered: 3, notAnswered: 3, absent: { no_page: 1 } },
    siteAnswersAbsentDetail: {
        no_page: "Search Console reported no page for this search, so there is no page of yours to judge.",
    },
};

/** Every fixture business shares one block: it is a demo, not a measurement. */
export function getSearchQuestions(businessId: string): AeoSearchQuestions {
    void businessId;
    return FIXTURE_SEARCH_QUESTIONS;
}

// ─── Search performance (`results.searchPerformance`) ──────────────────────
//
// A fixture business has no Search Console, so without this the staff Search
// performance screen previews as its not-connected state. The figures hang
// together the way a real pull does: the brand and non-brand rollups sum to
// the totals, the bands are cumulative over non-branded searches only, the
// keywords are a subset of the searches behind the totals (Google withholds
// rare ones), and one search is new this window, so it carries no movement.

const FIXTURE_SEARCH_PERFORMANCE: AeoSearchPerformance = {
    connected: true,
    searchScore: 62,
    // The 28 days Search Console had closed by the sample scan (16 Sep): it
    // reports 2 to 3 days behind, so the window ends on the 14th.
    window: { start: "2026-08-18", end: "2026-09-14", days: 28 },
    previousWindow: { start: "2026-07-21", end: "2026-08-17", days: 28 },
    totals: { clicks: 414, impressions: 9480, ctr: 0.0437, avgPosition: 9.4, clicksDeltaPct: 0.156, impressionsDeltaPct: 0.082 },
    buckets: { top3: 5, top10: 24, top20: 39, beyond20: 141 },
    branded: { clicks: 131, impressions: 520, ctr: 0.2519 },
    nonBranded: { clicks: 283, impressions: 8960, ctr: 0.0316 },
    keywords: [
        { query: "peranakan restaurant near me", clicks: 14, impressions: 2100, ctr: 0.0067, position: 11.4, branded: false, positionDelta: -3.5 },
        { query: "ayam buah keluak", clicks: 18, impressions: 1450, ctr: 0.0124, position: 8.9, branded: false, positionDelta: 2.1 },
        { query: "peranakan restaurant bukit timah", clicks: 42, impressions: 1180, ctr: 0.0356, position: 4.1, branded: false, positionDelta: -1.2 },
        { query: "nyonya kueh singapore", clicks: 5, impressions: 880, ctr: 0.0057, position: 22.6, branded: false, positionDelta: 4 },
        { query: "nyonya food bukit timah", clicks: 31, impressions: 860, ctr: 0.036, position: 5.3, branded: false, positionDelta: -0.8 },
        { query: "chap chye recipe", clicks: 1, impressions: 720, ctr: 0.0014, position: 41.3, branded: false, positionDelta: 6.2 },
        { query: "peranakan set lunch", clicks: 9, impressions: 640, ctr: 0.0141, position: 14.2, branded: false, positionDelta: null },
        { query: "private dining bukit timah", clicks: 7, impressions: 520, ctr: 0.0135, position: 12.8, branded: false, positionDelta: -1 },
        { query: "nyonya table", clicks: 96, impressions: 410, ctr: 0.2341, position: 1.2, branded: true, positionDelta: 0 },
        { query: "peranakan catering", clicks: 4, impressions: 410, ctr: 0.0098, position: 18.1, branded: false, positionDelta: -2.4 },
        { query: "halal peranakan food", clicks: 2, impressions: 310, ctr: 0.0065, position: 18.2, branded: false, positionDelta: 1.1 },
        { query: "nyonya table bukit timah", clicks: 35, impressions: 110, ctr: 0.3182, position: 1.4, branded: true, positionDelta: -0.2 },
    ],
    pages: [
        { url: "https://nyonyatable.com/", clicks: 188, impressions: 3920, ctr: 0.048, position: 6.1 },
        { url: "https://nyonyatable.com/menu", clicks: 74, impressions: 2210, ctr: 0.0335, position: 8.8 },
        { url: "https://nyonyatable.com/locations/bukit-bintang", clicks: 61, impressions: 1340, ctr: 0.0455, position: 5.2 },
        { url: "https://nyonyatable.com/private-dining", clicks: 29, impressions: 760, ctr: 0.0382, position: 11.9 },
        { url: "https://nyonyatable.com/catering", clicks: 22, impressions: 540, ctr: 0.0407, position: 16.3 },
        { url: "https://nyonyatable.com/faqs", clicks: 17, impressions: 380, ctr: 0.0447, position: 13.4 },
        { url: "https://nyonyatable.com/booking", clicks: 15, impressions: 210, ctr: 0.0714, position: 4.6 },
        { url: "https://nyonyatable.com/about", clicks: 8, impressions: 120, ctr: 0.0667, position: 9.7 },
    ],
    pagesWithImpressions: 14,
    // The sample crawl's own count (Site shows 32), so the two screens agree.
    pagesCrawled: 32,
    fetchedAt: "2026-09-16T01:00:00Z",
};

/** Every fixture business shares one block, as with the searches above. */
export function getSearchPerformance(businessId: string): AeoSearchPerformance {
    void businessId;
    return FIXTURE_SEARCH_PERFORMANCE;
}

const queuedFills: Record<string, AeoQueuedFill[]> = {};

export function getQueuedFills(businessId: string): Promise<AeoQueuedFill[]> {
    return new Promise((r) => setTimeout(() => r(queuedFills[businessId] ?? []), 150));
}

export function queueQuestionFill(
    businessId: string,
    fill: { variable: string; value: string; replaces?: string | null },
): Promise<{ ok: true } | { ok: false; kind: "full"; message: string }> {
    const held = queuedFills[businessId] ?? [];
    // The same five-at-a-time rule the server enforces, so the full state is
    // reachable in a demo rather than only in production.
    if (held.length >= 5) {
        return new Promise((r) => setTimeout(() => r({
            ok: false, kind: "full",
            message: "Five changes are already queued for the next scan.",
        }), 200));
    }
    queuedFills[businessId] = [...held, {
        variable: fill.variable, value: fill.value, replaces: fill.replaces ?? null,
        queuedAt: new Date().toISOString(), queuedBy: "demo",
    }];
    return new Promise((r) => setTimeout(() => r({ ok: true }), 200));
}

export function unqueueQuestionFill(businessId: string, variable: string): Promise<boolean> {
    queuedFills[businessId] = (queuedFills[businessId] ?? []).filter((q) => q.variable !== variable);
    return new Promise((r) => setTimeout(() => r(true), 150));
}

/* ── Partial re-run (ourteam-backend#5059) ────────────────────────────────── */
//
// The demo runs the job for two polls and lands. Enough to exercise the sticky
// in-flight state and the confirm dialog without a backend; `kind` follows the
// stages, the way the real endpoint decides it.

const stageJobs: Record<string, AeoStageRerunJob> = {};

// The four that ask the world again. `finalize` only recomputes what is
// stored, so it is free — the same split the real endpoint reports.
const REMEASURE_STAGES = ["crawl", "gsc", "ga4", "pagespeed"];

export function startStageRerun(assessmentId: string, stages: string[]) {
    void assessmentId;
    const jobId = `demo-rerun-${Date.now()}`;
    stageJobs[jobId] = { status: "running", stages };
    // Lands on the second poll, so the demo shows a real in-flight state.
    setTimeout(() => { stageJobs[jobId] = { status: "done", stages }; }, 3_000);
    const remeasure = stages.some((s) => REMEASURE_STAGES.indexOf(s) >= 0);
    return new Promise<{ ok: true; jobId: string; stages: string[]; kind: AeoRerunKind; estimatedCostUsd: number }>(
        (r) => setTimeout(() => r({
            ok: true,
            jobId,
            stages,
            kind: remeasure ? "remeasure" : "recompute",
            estimatedCostUsd: remeasure ? 0.41 : 0,
        }), 250),
    );
}

export function getStageRerun(assessmentId: string, jobId: string): Promise<AeoStageRerunJob | "gone"> {
    void assessmentId;
    return new Promise((r) => setTimeout(() => r(stageJobs[jobId] ?? "gone"), 200));
}

/* ── Google AI refresh (ourteam-frontend#3922) ────────────────────────────── */
//
// The demo runs a read for three polls and lands a measured row whose coverage
// is deliberately incomplete: Google showed no overview for 5 of the 50, and 2
// could not be read. Those are the two states the screen gets wrong if it
// treats either as a zero, so they are the ones the fixture has to contain.

let googleAiJob: { id: string; job: AeoGoogleAiRefreshJob } | null = null;

const DEMO_GOOGLE_AI_ROW: AeoEngineShare = {
    engine: "Google AI",
    instrument: "google_serp",
    isChatgptApp: false,
    citationOnly: false,
    probed: true,
    mentions: 7,
    answered: 43,
    sharePct: 16.3,
    sharePctBasis: "engine_hit_rate",
    summary: "Named in 7 of the 43 AI Overviews Google rendered; Google showed no AI Overview for 5 of 50 questions",
    rivals: ["COCOdry", "Svenson Hair Centre"],
    coverage: { asked: 50, answered: 43, notShown: 5, failed: 2 },
    capturedAt: new Date().toISOString(),
    country: "MY",
};

export function startGoogleAiRefresh(businessId: string): Promise<AeoGoogleAiRefreshStart> {
    void businessId;
    const id = `demo-googleai-${Date.now()}`;
    let polls = 0;
    googleAiJob = { id, job: { status: "running", error: null, startedAt: new Date().toISOString(), mode: "read" } };
    const tick = setInterval(() => {
        polls += 1;
        if (polls >= 3 && googleAiJob?.id === id) {
            googleAiJob.job = { status: "done", error: null, mode: "read", googleAi: DEMO_GOOGLE_AI_ROW };
            clearInterval(tick);
        }
    }, 2_000);
    return new Promise((r) => setTimeout(() => r({ ok: true, jobId: id, questions: 50 }), 300));
}

export function getGoogleAiRefreshState(businessId: string): Promise<AeoGoogleAiRefreshState> {
    void businessId;
    const live = googleAiJob && (googleAiJob.job.status === "running" || googleAiJob.job.status === "queued");
    return new Promise((r) => setTimeout(() => r(live
        ? { status: "running", jobId: googleAiJob!.id, startedAt: googleAiJob!.job.startedAt }
        : { status: "idle" }), 150));
}

export function getGoogleAiRefreshJob(
    businessId: string,
    jobId: string,
): Promise<AeoGoogleAiRefreshJob | "gone"> {
    void businessId;
    return new Promise((r) => setTimeout(
        () => r(googleAiJob?.id === jobId ? googleAiJob.job : "gone"), 200));
}
